/*
 * fn: fetchCardBatch
 *
 * Given an array of cardIds, sends a request to the Card API batch endpoint
 *
 */
import { type CardBatchResponseType } from '@/defs/apiLegacy/cardResponse'
import { type CardIdType, type CardMapType } from '@/defs/card'
import { parseCardId } from '@/utils/cardUtil'
import { fetchApi } from '@/utils/requestUtil'

import { parseCardResponse } from './parseCardResponse'

type Props = {
  cardIds: CardIdType[]
}

export const fetchCardBatch = async ({ cardIds }: Props) => {
  const requestIds = cardIds.map((cardId) => {
    const { collectorNumber, set, slug } = parseCardId(cardId)
    return { slug, ...(set ? { set } : {}), ...(collectorNumber ? { collector_number: collectorNumber } : {}) }
  })

  const { data } = await fetchApi<CardBatchResponseType>({
    base: 'api',
    options: {
      body: JSON.stringify({ cards: requestIds }),
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      method: 'POST',
    },
    path: '/cards2',
  })

  const responses = data.data || []
  const cardMap: CardMapType = {}

  if (responses.length !== cardIds.length) {
    console.error(`[apiUtil/fetchCardBatch] Api response length mismatch: ${responses.length} vs ${cardIds.length}`)
    return cardMap
  }

  for (const [i, cardId] of cardIds.entries()) {
    const response = responses[i]

    if (!response) {
      console.error(`[apiUtil/fetchCardBatch] Card not found: ${cardId}`)
      continue
    }

    if ('error' in response) {
      console.error(`[apiUtil/fetchCardBatch] Card error for ${cardId}: ${response.error}`)
      continue
    }

    cardMap[cardId] = parseCardResponse(response)
  }

  return cardMap
}
